import { useAppStore } from '../store/appStore';
import type { UserRole } from '../api/auth.api';

/**
 * UI-side role checks, used to hide controls a user can't use.
 *
 * This is NOT the security boundary — the backend enforces every rule with a
 * 403 regardless of what the frontend renders (see FRONTEND_RBAC_GUIDE.md §3).
 * Hiding a button is purely about not dead-ending the user.
 */
export function useHasRole(...allowed: UserRole[]): boolean {
  const user = useAppStore((s) => s.user);
  if (!user) return false;
  if (user.role === 'SUPER_ADMIN') return true; // bypasses everything, matches backend
  return allowed.includes(user.role);
}

/** True for the roles that can manage users, billing and org settings. */
export function useIsOwner(): boolean {
  return useHasRole('ORGANIZATION_OWNER');
}

/**
 * True for the lead-desk roles — LEAD_MANAGER and MEMBER — whose nav is scoped
 * down to Leads and Conversations, plus the dashboard for MEMBER (see the
 * `*.view` entries below; `dashboard.view` is what separates the two).
 *
 * Reads `LEAD_DESK` rather than naming the roles again, so it can't drift from
 * the matrix that actually hides the screens.
 *
 * Deliberately not built on `useHasRole`, which lets SUPER_ADMIN through: this
 * picks which panel the dashboard renders, and platform staff should see the
 * standard one.
 */
export function useIsLeadDesk(): boolean {
  const role = useAppStore((s) => s.user?.role);
  return !!role && LEAD_DESK.includes(role);
}

/** The current user's role, or null when not signed in. */
export function useCurrentRole(): UserRole | null {
  return useAppStore((s) => s.user?.role ?? null);
}

/**
 * The capabilities the UI gates on. Each one mirrors the `@Roles(...)`
 * decorator on the backend route(s) it stands for, so the buttons we show
 * match the calls that will actually succeed:
 *
 *   workspaces.manage   POST/PATCH /workspaces
 *   agents.manage       POST/PATCH/DELETE /agents (+ avatar upload)
 *   channels.manage     /agents/:id/whatsapp (whole controller) and
 *                       POST/PATCH /agents/:id/voice
 *   channels.whatsappVoice  the WhatsApp and Voice rows on the Channels
 *                       screen. Same backend guards as channels.manage; it
 *                       only exists so CUSTOMER can hold Channels with Web
 *                       Chat alone (UI-only, see below).
 *   automations.access  /agents/:id/automations, /automations/* (whole
 *                       controllers — reads are guarded too)
 *   logs.access         /logs (whole controller — reads are guarded too)
 *   knowledge.delete    DELETE /knowledge-sources/:id
 *   leads.delete        DELETE /crm/leads/:id
 *   leads.collectors    POST /crm/leads/lead-form-tokens, GET collectors
 *   escalations.delete  DELETE /escalations/:id, DELETE /support-tickets/:id
 *   users.manage        POST /users/invite, PATCH /users/:id[/status]
 *                       [/reset-password]
 *
 * `actions.manage` is the one entry with no backend guard behind it — the
 * agent-tools and identity controllers are JWT-only today. It's gated here
 * because agent Actions are part of agent configuration, which the roles doc
 * puts out of reach for the lead-desk roles. Treat that as a UI-only
 * restriction until the backend catches up.
 *
 * The `*.view` / `*.access` entries below are a second, narrower kind of
 * entry: they don't mirror a write guard, they say whether a role has any
 * business on a screen at all. LEAD_MANAGER and MEMBER are both lead-desk
 * roles, so the agent builder, the voice agent, workspace settings, Users and
 * Support Tickets are taken out of their nav and off their routes:
 *
 *   agents.view              /workspaces/:id/agents[/...] (list, details,
 *                            training, chat preview)
 *   voice.access             /workspaces/:id/agents/:agentId/voice-agent
 *   workspaceSettings.access /workspaces/:id (the Settings screen)
 *   users.view               /users
 *   escalations.access       /escalations (Support Tickets)
 *   dashboard.view           /dashboard
 *
 * `leads.view` (/leads) is the same kind of entry, held by every role except
 * CUSTOMER (see below).
 *
 * `conversations.view` (/workspaces/:id/agents/:agentId/conversations) sits in
 * the same family but is granted to every role: reading a conversation is how
 * the lead desk sees what its leads actually said, and the backend has never
 * gated those reads. It keeps its entry so the screen has a name to narrow by
 * if that changes.
 *
 * These are UI scoping, not privilege: GET /agents, GET /workspaces and the
 * conversation reads are open to every role on the backend, and the delete
 * routes still accept a LEAD_MANAGER (which is why `escalations.delete` below
 * keeps the role). Nothing here can be relied on as a security boundary.
 *
 * The two lead-desk roles are not the same size, and `dashboard.view` is what
 * separates them:
 *
 *   LEAD_MANAGER   Leads and Conversations. The narrowest role in the app;
 *                  Leads is where it lands after login.
 *   MEMBER         Dashboard, Leads and Conversations.
 *
 * MEMBER otherwise mirrors LEAD_MANAGER with one deliberate exception:
 * `leads.status`. Both roles get the same deletes, but a MEMBER reads lead
 * status rather than setting it — the Leads table renders a plain badge for
 * that column instead of the dropdown every other role gets.
 *
 * `leads.status` is a UI-only restriction, and in the opposite direction from
 * the note above: PATCH /crm/leads/:id is JWT-only on the backend, so a MEMBER
 * can still change a status by calling the API directly. It is a product rule
 * about who drives the funnel, not a boundary — don't treat it as one.
 *
 * The deletes cut the other way: the backend `@Roles(...)` guards still name
 * LEAD_MANAGER and not MEMBER, so the delete buttons this matrix shows a
 * MEMBER will come back 403 until the backend catches up. That is a UI-only
 * grant, the same caveat `actions.manage` carries above.
 *
 * CUSTOMER is the website demo (trial) sign-up. The backend treats it as an
 * owner (backend/src/common/owner-roles.ts), but the dashboard scopes it down
 * to the agent builder (`agents.view`, `agents.manage`, `knowledge.delete`),
 * Channels (Web Chat only — no `channels.whatsappVoice`) and Conversations.
 * Voice Agent, workspaces, actions, automations, settings, users, tickets,
 * leads, logs and the dashboard stay out of its nav. The agent list is where it lands after login (useAuth.ts). Like the
 * lead-desk scoping, this is UI only: the API would still let it do more.
 */
export type Permission =
  | 'workspaces.manage'
  | 'agents.manage'
  | 'actions.manage'
  | 'channels.manage'
  | 'channels.whatsappVoice'
  | 'automations.access'
  | 'logs.access'
  | 'knowledge.delete'
  | 'leads.delete'
  | 'leads.status'
  | 'leads.collectors'
  | 'escalations.delete'
  | 'users.manage'
  | 'agents.view'
  | 'conversations.view'
  | 'voice.access'
  | 'workspaceSettings.access'
  | 'users.view'
  | 'escalations.access'
  | 'dashboard.view'
  | 'leads.view';

/**
 * The lead-desk roles — the ones scoped down to the CRM rather than the agent
 * builder. They see Leads and Conversations (and, for MEMBER, the dashboard)
 * and nothing else, and useAuth.ts reads this matrix rather than the role name
 * to decide where each one lands after login.
 *
 * Declared once and spread into the sets below so adding or removing a role
 * here moves its grants *and* its screen scoping together — the two used to be
 * written out separately, which is exactly how a role ends up with a delete
 * button on a screen it can no longer open.
 */
const LEAD_DESK: readonly UserRole[] = ['LEAD_MANAGER', 'MEMBER'];

const OWNER_ADMIN: readonly UserRole[] = ['ORGANIZATION_OWNER', 'ADMIN'];
const OWNER_ADMIN_LEAD_DESK: readonly UserRole[] = [...OWNER_ADMIN, ...LEAD_DESK];
/**
 * The demo sign-up role, scoped to the agent builder, Channels (Web Chat only)
 * and Conversations. Spread only into the entries for those screens.
 */
const CUSTOMER: readonly UserRole[] = ['CUSTOMER'];
const OWNER_ADMIN_CUSTOMER: readonly UserRole[] = [...OWNER_ADMIN, ...CUSTOMER];
/**
 * Every role. Used by the screens that are open to the whole organization — the
 * entry stays in the matrix rather than dropping its gate, so narrowing it
 * later is a one-line change here instead of re-threading a permission through
 * the nav and the route.
 */
const EVERY_ROLE: readonly UserRole[] = [...OWNER_ADMIN, ...LEAD_DESK, ...CUSTOMER];
/**
 * Everyone but MEMBER — the one entry where the two lead-desk roles differ.
 * Spelled as "the lead desk minus MEMBER" rather than listing three roles, so
 * a role added to LEAD_DESK is included here by default and has to be taken
 * out on purpose.
 */
const EXCEPT_MEMBER: readonly UserRole[] = [
  ...OWNER_ADMIN,
  ...LEAD_DESK.filter((role) => role !== 'MEMBER'),
];
/**
 * Everyone but LEAD_MANAGER — that role's whole surface is Leads, so the
 * dashboard comes out of its nav and off its route as well. Built the same way
 * as EXCEPT_MEMBER: the lead desk minus one role, not three roles listed out.
 */
const EXCEPT_LEAD_MANAGER: readonly UserRole[] = [
  ...OWNER_ADMIN,
  ...LEAD_DESK.filter((role) => role !== 'LEAD_MANAGER'),
];
const OWNER_ONLY: readonly UserRole[] = ['ORGANIZATION_OWNER'];
/**
 * Everyone outside the lead desk. Used by the `*.view` / `*.access` entries,
 * which scope the lead-desk roles down to their two screens rather than
 * granting a capability.
 */
const OUTSIDE_LEAD_DESK: readonly UserRole[] = OWNER_ADMIN;

export const PERMISSIONS: Record<Permission, readonly UserRole[]> = {
  'workspaces.manage': OWNER_ADMIN,
  'agents.manage': OWNER_ADMIN_CUSTOMER,
  'actions.manage': OWNER_ADMIN,
  'channels.manage': OWNER_ADMIN_CUSTOMER,
  'channels.whatsappVoice': OWNER_ADMIN,
  'automations.access': OWNER_ADMIN,
  'logs.access': OWNER_ADMIN,
  'knowledge.delete': OWNER_ADMIN_CUSTOMER,
  'leads.delete': OWNER_ADMIN_LEAD_DESK,
  'leads.status': EXCEPT_MEMBER,
  'leads.collectors': OWNER_ADMIN,
  'escalations.delete': OWNER_ADMIN_LEAD_DESK,
  'users.manage': OWNER_ONLY,
  'agents.view': OWNER_ADMIN_CUSTOMER,
  'conversations.view': EVERY_ROLE,
  'voice.access': OUTSIDE_LEAD_DESK,
  'workspaceSettings.access': OUTSIDE_LEAD_DESK,
  'users.view': OUTSIDE_LEAD_DESK,
  'escalations.access': OUTSIDE_LEAD_DESK,
  'dashboard.view': EXCEPT_LEAD_MANAGER,
  'leads.view': OWNER_ADMIN_LEAD_DESK,
};

/**
 * Pure form of the check, for the places that already hold a role (route
 * guards, table rows) and for testing the matrix without a store.
 */
export function can(role: UserRole | null | undefined, permission: Permission): boolean {
  if (!role) return false;
  if (role === 'SUPER_ADMIN') return true; // bypasses everything, matches backend
  return PERMISSIONS[permission].includes(role);
}

/** Whether the signed-in user holds a capability. */
export function useCan(permission: Permission): boolean {
  const role = useAppStore((s) => s.user?.role ?? null);
  return can(role, permission);
}

/** Human-readable label for a role value, for read-only cells and badges. */
export function roleLabel(role: UserRole): string {
  switch (role) {
    case 'SUPER_ADMIN':
      return 'Super Admin';
    case 'ORGANIZATION_OWNER':
      return 'Organization Owner';
    case 'ADMIN':
      return 'Admin';
    case 'LEAD_MANAGER':
      return 'Lead Manager';
    case 'MEMBER':
      return 'Member';
    case 'CUSTOMER':
      return 'Customer';
    default:
      return role;
  }
}
